import axios from '|/helpers/axios';
import {
  PERSONAL_DETAILS_ENDPOINTS,
  DRIVERS_LICENSE_ENDPOINTS,
  DOC_VISION_ENDPOINT,
  EV_TAX_CREDIT_ENDPOINTS
} from './endpoints';
import {
  Addresses,
  AddressData,
  Demographics,
  Normalization,
  ValidationBlocks,
  PersonalDetails,
  DriversLicenseName,
  DriversLicenseResponse,
  DriversLicenseUploadResponse,
  DriversLicenseNameUpdateResponse,
  DriversLicenseImage,
  DocVisionResponse,
  CleanVehicleTaxCreditInfoResponse
} from './types';

export const getPersonalDetails = async (abortSignal: AbortSignal) =>
  axios
    .get<PersonalDetails>(PERSONAL_DETAILS_ENDPOINTS.GET_PERSONAL_DETAILS, {
      signal: abortSignal
    })
    .then(res => res.data);

export const validateAddresses = async (addresses: Addresses, abortSignal: AbortSignal) =>
  axios
    .post<ValidationBlocks>(PERSONAL_DETAILS_ENDPOINTS.VALIDATE_ADDRESSES, addresses, {
      headers: {
        'Content-Type': 'application/json'
      },
      signal: abortSignal
    })
    .then(res => res.data);

export const normalizeAddress = async (address: AddressData, abortSignal: AbortSignal) =>
  axios
    .post<Normalization>(PERSONAL_DETAILS_ENDPOINTS.NORMALIZE_ADDRESSES, address, {
      headers: {
        'Content-Type': 'application/json'
      },
      signal: abortSignal
    })
    .then(res => res.data);

export const saveDemographics = async (
  demographics: Demographics,
  abortSignal: AbortSignal
): Promise<void> => {
  return axios
    .post<never>(PERSONAL_DETAILS_ENDPOINTS.SAVE_DEMOGRAPHICS, demographics, {
      headers: {
        'Content-Type': 'application/json'
      },
      signal: abortSignal
    })
    .then(() => undefined);
};

export const saveAddresses = async (
  addresses: Addresses,
  abortSignal: AbortSignal
): Promise<void> => {
  return axios
    .post<never>(PERSONAL_DETAILS_ENDPOINTS.SAVE_ADDRESSES, addresses, {
      headers: {
        'Content-Type': 'application/json'
      },
      signal: abortSignal
    })
    .then(() => undefined);
};

export const saveDeliveryAddress = async (
  deliveryAddress: AddressData,
  abortSignal: AbortSignal
): Promise<void> => {
  return axios
    .post<never>(PERSONAL_DETAILS_ENDPOINTS.SAVE_DELIVERY_ADDRESS, deliveryAddress, {
      headers: {
        'Content-Type': 'application/json'
      },
      signal: abortSignal
    })
    .then(() => undefined);
};

export const getDriversLicense = async (
  abortSignal: AbortSignal
): Promise<DriversLicenseResponse[]> => {
  return axios
    .get<DriversLicenseResponse[]>(DRIVERS_LICENSE_ENDPOINTS.GET_DRIVERS_LICENSE, {
      signal: abortSignal
    })
    .then(res => res.data);
};

export const getDriversLicenseByDocumentId = async (
  documentId: string,
  abortSignal: AbortSignal
): Promise<DriversLicenseImage> => {
  return axios
    .get<DriversLicenseImage>(
      `${DRIVERS_LICENSE_ENDPOINTS.GET_DRIVERS_LICENSE_IMAGE}/${documentId}`,
      {
        signal: abortSignal
      }
    )
    .then(res => res.data);
};

export const uploadDriversLicense = async (
  inquiryId: string,
  isCobuyer: boolean,
  abortSignal: AbortSignal
): Promise<DriversLicenseUploadResponse> => {
  return axios
    .post<DriversLicenseUploadResponse>(
      DRIVERS_LICENSE_ENDPOINTS.UPLOAD_DRIVERS_LICENSE,
      {
        inquiryId,
        isCobuyer
      },
      {
        headers: {
          'Content-Type': 'application/json'
        },
        signal: abortSignal
      }
    )
    .then(res => res.data);
};

export const validateDriversLicense = async (
  document: File,
  documentFormat: string,
  abortSignal: AbortSignal
): Promise<DocVisionResponse> => {
  const formData = new FormData();
  formData.append('document', document);
  formData.append('documentFormat', documentFormat);

  return axios
    .post<DocVisionResponse>(
      DOC_VISION_ENDPOINT.VALIDATE_DOCUMENT,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal: abortSignal
      }
    )
    .then(res => res.data);
};

export const uploadDocuments = async (
  frontImage: File,
  backImage: File,
  isCobuyer: boolean,
  abortSignal: AbortSignal
): Promise<DriversLicenseUploadResponse> => {
  const formData = new FormData();
  formData.append('frontImage', frontImage);
  formData.append('backImage', backImage);
  formData.append('isCobuyer', String(isCobuyer));

  return axios
    .post<DriversLicenseUploadResponse>(
      DRIVERS_LICENSE_ENDPOINTS.UPLOAD_FALLBACK,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal: abortSignal
      }
    )
    .then(res => res.data);
};

export const updateDriversLicenseName = async (
  body: DriversLicenseName,
  abortSignal: AbortSignal
): Promise<DriversLicenseNameUpdateResponse> => {
  return axios
    .post<DriversLicenseNameUpdateResponse>(DRIVERS_LICENSE_ENDPOINTS.UPDATE_DRIVERS_LICENSE_NAME, body, {
      headers: {
        'Content-Type': 'application/json'
      },
      signal: abortSignal
    })
    .then(res => res.data);
};

export const getCleanVehicleTaxCreditInfo = async (
  abortSignal: AbortSignal
): Promise<CleanVehicleTaxCreditInfoResponse> =>
  axios
    .get<CleanVehicleTaxCreditInfoResponse>(EV_TAX_CREDIT_ENDPOINTS.GET_EV_TAX_CREDIT_INFO, {
      signal: abortSignal
    })
    .then(res => res.data);

export const submitCleanTaxCreditOption = async (
  cleanTaxCredit: boolean,
  abortSignal: AbortSignal
): Promise<void> => {
  return axios
    .post<never>(
      EV_TAX_CREDIT_ENDPOINTS.SUBMIT_EV_TAX_CREDIT_ELIGIBILITY,
      { isEvTaxCreditEligible: cleanTaxCredit },
      {
        headers: {
          'Content-Type': 'application/json'
        },
        signal: abortSignal
      }
    )
    .then(() => undefined);
};
